import React, { useState, useEffect } from "react";
import { Layout, Typography, Space, Modal, Badge, Menu } from "antd";
import {
  AppstoreOutlined,
  TeamOutlined,
  UserOutlined,
  SettingOutlined,
  CalendarOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  DashboardOutlined,
  ScheduleOutlined,
  SolutionOutlined,
  ProfileOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchNotification } from "../../Redux/Slice/Doctor/callSlice";
import VideoCallDrawer from "./VideoCallDrawer";
import { toast } from "react-toastify";
import { logoutUser } from "../../Redux/Slice/Login/Logout";
import "./blink.css";
import { fetchOrg } from "../../Redux/Slice/Admin/Org";
import { Dropdown } from "antd";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AppLayout = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(true);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const userType = userData?.roleName || "guest";
  const username = userData?.userName || "Guest";
  const userId = userData?.userId;

  const branches = JSON.parse(localStorage.getItem("branches")) || [];
  const [selectedBranchId, setSelectedBranchId] = useState(
    localStorage.getItem("selectedBranchId") || branches[0]?.branchId || null
  );

  const handleBranchSwitch = (branchId) => {
    localStorage.setItem("selectedBranchId", branchId);
    setSelectedBranchId(branchId);
    window.location.reload(); // 🔄 ensure all API calls use new branch
  };

  // Dropdown menu items
  const branchMenuItems = branches.map((branch) => ({
    key: branch.branchId,
    label: branch.branchName,
  }));

  const { loading, data } = useSelector((state) => state.call.notification);

  const {
    data: logoutData,
    loading: logoutLoading,
    error: logoutError,
  } = useSelector((state) => state.logout);

  useEffect(() => {
    dispatch(fetchOrg());
  }, [dispatch]);

  const { data: orgData } = useSelector((state) => state.org);

  useEffect(() => {
    if (logoutData?.status) {
      toast.success(logoutData?.message || "Logout successfully");

      localStorage.clear();

      // 🔄 Hard refresh and force redirect to login
      window.location.href = "/login";
    } else if (logoutError) {
      toast.error(logoutError?.message || "Something went wrong");
    }
  }, [logoutData, logoutError]);

  const logout = () => {
    dispatch(logoutUser(userId));
  };

  const pathToKey = {
    "/dashboard": "dashboard",
    "/org": "org",
    "/role": "role",
    "/doctors": "doctors",
    "/appointments": "appointments",
    "/prescriptions": "prescriptions",
    "/records": "records",
    "/patient-reports": "patient-reports",
    "/doc-dashboard": "doc-dashboard",
    "/patients": "patients",
    "/patient-dashboard": "patient-dashboard",
    "/patient-records": "patient-records",
    "/login": "login",
    "/patient-appointment": "patient-appointment",
    "/book-appointment": "book-appointment",
    "/lab": "lab",
    "/medicine-master": "medicine-master",
    "/recp-dashboard": "recp-dashboard",
    "/booking": "booking",
  };

  const menuItemsByRole = {
    ADMIN: [
      {
        key: "dashboard",
        icon: <AppstoreOutlined />,
        label: <Link to="/dashboard">Dashboard</Link>,
        group: "Admin Management",
      },
      {
        key: "org",
        icon: <SettingOutlined />,
        label: <Link to="/org">Org</Link>,
        group: "Admin Management",
      },
      {
        key: "role",
        icon: <UserOutlined />,
        label: <Link to="/role">Role</Link>,
        group: "Admin Management",
      },
      {
        key: "doctors",
        icon: <TeamOutlined />,
        label: <Link to="/doctors">Doctors</Link>,
        group: "Admin Management",
      },
      {
        key: "lab",
        icon: <ExperimentOutlined />,
        label: <Link to="/lab">Lab</Link>,
        group: "Admin Management",
      },
      {
        key: "medicine-master",
        icon: <MedicineBoxOutlined />,
        label: <Link to="/medicine-master">Medicines</Link>,
        group: "Admin Management",
      },
    ],
    Doctor: [
      {
        key: "doc-dashboard",
        icon: <AppstoreOutlined />,
        label: <Link to="/doc-dashboard">Dashboard</Link>,
        group: "Doctor Panel",
      },
      {
        key: "appointments",
        icon: <CalendarOutlined />,
        label: <Link to="/appointments">Appointments</Link>,
        group: "Doctor Panel",
      },
    ],
    Receptionist: [
      {
        key: "recp-dashboard",
        icon: <AppstoreOutlined />,
        label: <Link to="/recp-dashboard">Dashboard</Link>,
        group: "Receptionist Panel",
      },
      {
        key: "booking",
        icon: <CalendarOutlined />,
        label: <Link to="/booking">Booking</Link>,
        group: "Receptionist Panel",
      },
    ],
    Patient: [
      {
        key: "patient-dashboard",
        icon: <DashboardOutlined />,
        label: <Link to="/patient-dashboard">Dashboard</Link>,
        group: "Patient Dashboard",
      },
      {
        key: "book-appointment",
        icon: <ScheduleOutlined />,
        label: <Link to="/book-appointment">Book Appointments</Link>,
        group: "Patient Dashboard",
      },
      {
        key: "patient-appointment",
        icon: <SolutionOutlined />,
        label: <Link to="/patient-appointment">My Appointments</Link>,
        group: "Patient Dashboard",
      },
      {
        key: "prescriptions",
        icon: <MedicineBoxOutlined />,
        label: <Link to="/prescriptions">Medications</Link>,
        group: "Patient Dashboard",
      },
      {
        key: "patient-reports",
        icon: <FileTextOutlined />,
        label: <Link to="/patient-reports">My Reports</Link>,
        group: "Patient Dashboard",
      },
      {
        key: "patient-records",
        icon: <ProfileOutlined />,
        label: <Link to="/patient-records">My Records</Link>,
        group: "Patient Dashboard",
      },
    ],
    guest: [
      {
        key: "login",
        icon: <UserOutlined />,
        label: <Link to="/login">Login</Link>,
        group: "",
      },
    ],
  };

  const getCurrentGroupLabel = () => {
    const currentKey = pathToKey[location.pathname];
    const roleMenus = menuItemsByRole[userType] || [];
    const found = roleMenus.find((item) => item.key === currentKey);
    return found?.group || "";
  };

  const targetId =
    userType === "Doctor" ? userData?.doctorId : userData?.patientId;

  useEffect(() => {
    if (targetId) {
      dispatch(
        fetchNotification({
          doctorId: userType === "Doctor" ? targetId : undefined,
          patientId: userType === "Patient" ? targetId : undefined,
        })
      );
    }
  }, [drawerOpen, targetId, userType, dispatch]);

  const calls = data?.createdVideoCalls || [];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Sidebar */}
      <Sider
        collapsible
        collapsed={collapsed}
        trigger={null}
        width={220}
        style={{
          background: "linear-gradient(180deg, #004d4d 0%, #006666 100%)",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          height: "100vh",
          zIndex: 1,
        }}
      >
        {orgData?.map((org) => (
          <div
            style={{
              height: 64,
              display: "flex",
              alignItems: "center",
              // justifyContent: "center",
              color: "white",
              fontWeight: "bold",
              fontSize: collapsed ? 18 : 20,
              gap: collapsed ? 0 : 3,
              flexDirection: "column",
              paddingTop: "10px",
              marginBottom: collapsed ? "10px" : "20px",
            }}
          >
            <img
              src="https://www.germanclinic.ae/_next/image/?url=%2Fassets%2Fimages%2Fmmc-logo.png&w=1920&q=75"
              alt="logo"
              width={40}
            />{" "}
            {collapsed ? "" : org?.hospitalName}
          </div>
        ))}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[pathToKey[location.pathname]]}
          items={menuItemsByRole[userType] || menuItemsByRole.guest}
          style={{
            fontSize: 15,
            fontWeight: 500,
            background: "transparent",
            borderRight: 0,
          }}
        />
      </Sider>

      <Layout
        style={{
          marginLeft: collapsed ? 80 : 220,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Header
          style={{
            background: "#ffffff",
            padding: "0 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            height: 64,
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span
              style={{ fontSize: "18px", cursor: "pointer", color: "#006666" }}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
            <Text strong style={{ fontSize: 20, color: "#006666" }}>
              {getCurrentGroupLabel()}
            </Text>
          </div>

          <Space size="middle" style={{ gap: "30px" }}>
            {/* 👤 Username */}
            <Text
              strong
              style={{
                color: "#000366ff",
                backgroundColor: "#cce6e6ff",
                padding: "4px 8px",
                borderRadius: 4,
              }}
            >
              {username}
            </Text>

            {/* 🏥 Branch Switcher */}
            {branches.length > 0 && (
              <Dropdown
                menu={{
                  items: branchMenuItems,
                  onClick: ({ key }) => handleBranchSwitch(key),
                }}
                placement="bottom"
              >
                <Text
                  strong
                  style={{
                    cursor: "pointer",
                    color: "#006666",
                    backgroundColor: "#e6f7f7",
                    padding: "4px 8px",
                    borderRadius: 4,
                  }}
                >
                  {branches.find(
                    (b) => b.branchId.toString() === selectedBranchId
                  )?.branchName || "Select Branch"}
                </Text>
              </Dropdown>
            )}

            {/* 🔔 VideoCall Drawer */}
            <Badge
              className="blink-badge"
              count={
                calls.filter(
                  (c) => c.status !== "ended" && c.status !== "auto_ended"
                ).length
              }
            >
              <BellOutlined
                style={{ fontSize: 20, color: "#006666", cursor: "pointer" }}
                onClick={() => setDrawerOpen(true)}
              />
            </Badge>

            {/* 🚪 Logout */}
            <LogoutOutlined
              style={{ fontSize: 20, color: "#ef2222ff", cursor: "pointer" }}
              onClick={() => setLogoutModalVisible(true)}
            />
          </Space>
        </Header>

        <Content
          style={{
            margin: "16px",
            padding: 24,
            background: "#fff",
            overflow: "auto",
            flex: 1,
          }}
        >
          <Outlet />
        </Content>
      </Layout>

      {/* Logout Modal */}
      <Modal
        open={logoutModalVisible}
        title="Confirm Logout"
        onCancel={() => setLogoutModalVisible(false)}
        onOk={logout}
        okText="Logout"
        cancelText="Cancel"
        confirmLoading={logoutLoading}
      >
        <p>Are you sure you want to logout?</p>
      </Modal>

      {/* Drawer with API data */}
      <VideoCallDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        calls={loading ? [] : calls}
        userType={userType}
        loading={loading}
      />

      {/* {drawerOpen && (
        <Spin style={{ position: "absolute", top: "50%", left: "50%" }} />
      )} */}
    </Layout>
  );
};

export default AppLayout;
